import { useCallback, useEffect, useMemo, useState } from 'react';

import { Tag, TagsFilterSerialized } from '@playq/octopus-common';
import { TagPolicy, TagWithCounter, QueryIntelliTagResponse } from '@playq/octopus2-files';
import { GenericFailure } from '@playq/services-shared';

import { useMounted } from '/hooks/useMounted';
import { useAssetsIntelliTagsQuery, useAssetsPolicy } from '/api';

export const useIntelliTags = (tags?: Tag[]) => {
  const isMounted = useMounted();

  const [history, setHistory] = useState<QueryIntelliTagResponse[]>([]);

  const { policies } = useAssetsPolicy({
    staleTime: Infinity,
    enabled: isMounted.current === true,
    meta: { onError: (err: GenericFailure) => console.error(err) },
  });

  const otherTags = useMemo(() => {
    const intelliTags = history[history.length - 1];
    if (intelliTags !== undefined) {
      return intelliTags.other.sort((a, b) => b.count - a.count);
    }
    return [];
  }, [history]);

  const getPoliciesWithCounter = useCallback(
    (targetPolicies: TagPolicy[]) => {
      const lastPolicies = history[history.length - 1]?.policy;
      if (lastPolicies !== undefined) {
        return targetPolicies
          .reduce((acc: TagWithCounter[], policy) => {
            const policyWithCounter = lastPolicies.find((p) => p.key === policy.tag);
            if (policyWithCounter) {
              return acc.concat(policyWithCounter);
            }
            return acc;
          }, [])
          .sort((a, b) => b.count - a.count);
      }
      return [];
    },
    [history]
  );

  const requiredTags = useMemo(
    () => getPoliciesWithCounter(policies.filter((p) => p.required)),
    [policies, getPoliciesWithCounter]
  );
  const creationTags = useMemo(
    () => getPoliciesWithCounter(policies.filter((p) => !p.required)),
    [policies, getPoliciesWithCounter]
  );

  const tagsFilterSerialized: TagsFilterSerialized | undefined = useMemo(() => {
    if (!tags) {
      return undefined;
    }

    const result: TagsFilterSerialized = {
      includes: [],
      excludes: [],
    };

    tags.forEach((tag) => {
      if (tag.key.startsWith('-')) {
        result.excludes.push(
          new Tag({
            key: tag.key.substring(1),
            value: tag.value,
          })
        );
      } else {
        result.includes.push(tag);
      }
    });

    return result;
  }, [tags]);

  const { isLoading, intelliTags } = useAssetsIntelliTagsQuery(tagsFilterSerialized, {
    enabled: isMounted.current === true,
  });

  useEffect(() => {
    if (!isLoading && intelliTags !== undefined) {
      setHistory((h) => h.concat(intelliTags));
    }
  }, [intelliTags, isLoading]);

  return {
    policies,
    loading: isLoading,
    history,
    otherTags,
    requiredTags,
    creationTags,
  } as const;
};
